/**
 * QuotaWarningToast - 80% threshold warning toast
 *
 * Features:
 * - Prominent orange warning style
 * - Dismissible with X button
 * - Auto-dismiss after 8 seconds
 * - "View Plans" CTA button
 */
import { useState, useEffect } from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTranslation } from '@dramadetector/shared';

interface QuotaWarningToastProps {
  dailyAnalysesUsed: number;
  dailyAnalysesLimit: number;
  onDismiss: () => void;
  onViewPlans?: () => void;
}

export function QuotaWarningToast({
  dailyAnalysesUsed,
  dailyAnalysesLimit,
  onDismiss,
  onViewPlans,
}: QuotaWarningToastProps) {
  const { t } = useTranslation('subscription');
  const [visible, setVisible] = useState(true);

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      handleDismiss();
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    onDismiss();
  };

  if (!visible) return null;

  return (
    <View className="absolute top-4 left-4 right-4 z-50">
      <View className="bg-orange-100 border border-orange-300 rounded-lg p-4 shadow-lg">
        <View className="flex-row items-start mb-3">
          {/* Warning icon */}
          <Text className="text-xl mr-3">⚠️</Text>

          {/* Content */}
          <View className="flex-1">
            <Text className="text-sm font-semibold text-orange-900 mb-1">
              {t('quota.warning.toast.title')}
            </Text>
            <Text className="text-xs text-orange-800">
              {t('quota.warning.toast.description', {
                used: dailyAnalysesUsed,
                limit: dailyAnalysesLimit
              })}
            </Text>
          </View>

          {/* Dismiss button */}
          <Pressable
            onPress={handleDismiss}
            className="ml-2 w-6 h-6 items-center justify-center active:opacity-50"
            accessibilityRole="button"
            accessibilityLabel={t('quota.warning.toast.accessibility.dismiss')}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Text className="text-orange-400 text-lg">×</Text>
          </Pressable>
        </View>

        {/* View Plans CTA */}
        {onViewPlans && (
          <Pressable
            onPress={() => {
              onViewPlans();
              handleDismiss();
            }}
            className="bg-orange-600 py-2 px-4 rounded active:bg-orange-700"
            style={{ minHeight: 44 }}
            accessibilityRole="button"
            accessibilityLabel={t('quota.warning.toast.accessibility.view_plans')}
          >
            <Text className="text-white text-sm font-medium text-center">
              {t('quota.warning.toast.cta')}
            </Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}
